package scenes

import androidx.compose.animation.core.animateFloatAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.focusable
import androidx.compose.foundation.gestures.scrollBy
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.rememberScrollState
import androidx.compose.material3.Text
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.focus.FocusRequester
import androidx.compose.ui.focus.focusRequester
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.graphicsLayer
import androidx.compose.ui.input.key.*
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import domain.PlayGameMode
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import rendering.AnimatedBackground
import rendering.fontMono

private data class ModeCard(
    val mode: PlayGameMode,
    val label: String,
    val description: String,
    val color: Color,
    val action: () -> Unit,
)

@Composable
fun PlayScene(navigate: Navigate) {
    val focusRequester = remember { FocusRequester() }
    var selectedIndex by remember { mutableStateOf(0) }

    val monoStyle = TextStyle(fontFamily = fontMono(), letterSpacing = 0.05.sp)

    val cards = remember(navigate) {
        listOf(
            ModeCard(
                mode = PlayGameMode.SPRINT,
                label = "SPRINT",
                description = "Clear 40 lines\nas fast as possible",
                color = Color(0xFF00E5FF),
                action = { navigate(SceneName.GAME, NavigateOptions(mode = PlayGameMode.SPRINT)) },
            ),
            ModeCard(
                mode = PlayGameMode.TIMEATTACK,
                label = "TIME ATTACK",
                description = "Score as high as\npossible in 2 minutes",
                color = Color(0xFFFFD600),
                action = { navigate(SceneName.GAME, NavigateOptions(mode = PlayGameMode.TIMEATTACK)) },
            ),
            ModeCard(
                mode = PlayGameMode.STORY,
                label = "STORY",
                description = "Card-driven\nroguelite run",
                color = Color(0xFFAA00FF),
                action = { navigate(SceneName.STORY, null) },
            ),
            ModeCard(
                mode = PlayGameMode.CUSTOM,
                label = "CUSTOM",
                description = "Configure your\nown game",
                color = Color(0xFF00E676),
                action = { navigate(SceneName.CUSTOM_GAME, null) },
            ),
        )
    }

    val coroutineScope = rememberCoroutineScope()
    val scrollState = rememberScrollState()

    LaunchedEffect(Unit) {
        delay(50)
        focusRequester.requestFocus()
    }

    Box(
        modifier = Modifier
            .fillMaxSize()
            .background(Color.Black)
            .focusRequester(focusRequester)
            .focusable()
            .onKeyEvent { event ->
                if (event.type != KeyEventType.KeyDown) return@onKeyEvent false
                when (event.key) {
                    Key.DirectionLeft -> {
                        selectedIndex = (selectedIndex - 1 + cards.size) % cards.size
                        coroutineScope.launch { scrollState.scrollBy(-176f) }
                        true
                    }
                    Key.DirectionRight -> {
                        selectedIndex = (selectedIndex + 1) % cards.size
                        coroutineScope.launch { scrollState.scrollBy(176f) }
                        true
                    }
                    Key.Enter -> {
                        cards[selectedIndex].action()
                        true
                    }
                    Key.Escape -> {
                        navigate(SceneName.MENU, null)
                        true
                    }
                    else -> false
                }
            },
    ) {
        AnimatedBackground(modifier = Modifier.fillMaxSize())

        Column(
            modifier = Modifier.fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally,
            verticalArrangement = Arrangement.Center,
        ) {
            // Header
            Text(
                text = "SELECT MODE",
                style = monoStyle.copy(
                    fontSize = 14.sp,
                    color = Color(0xFF888888),
                    letterSpacing = 0.2.sp,
                ),
            )

            Spacer(modifier = Modifier.height(32.dp))

            // Mode cards — horizontally scrollable on mobile
            Row(
                modifier = Modifier
                    .horizontalScroll(scrollState)
                    .padding(horizontal = 24.dp),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                cards.forEachIndexed { index, card ->
                    val isSelected = index == selectedIndex
                    val liftY by animateFloatAsState(
                        targetValue = if (isSelected) -16f else 0f,
                        animationSpec = spring(dampingRatio = 0.5f, stiffness = 300f),
                    )

                    val borderColor = if (isSelected) card.color else Color(0xFF333355)
                    val titleColor = if (isSelected) card.color else Color(0xFF666688)

                    Box(
                        modifier = Modifier
                            .width(160.dp)
                            .height(200.dp)
                            .graphicsLayer { translationY = liftY }
                            .clickable { card.action() }
                            .background(Color(0xFF0A0A18))
                            .border(width = if (isSelected) 2.dp else 1.dp, color = borderColor)
                            .padding(20.dp),
                        contentAlignment = Alignment.TopStart,
                    ) {
                        Column {
                            Text(
                                text = card.label,
                                style = monoStyle.copy(
                                    fontSize = 14.sp,
                                    color = titleColor,
                                    letterSpacing = 0.1.sp,
                                ),
                            )
                            Spacer(modifier = Modifier.height(12.dp))
                            Text(
                                text = card.description,
                                style = monoStyle.copy(
                                    fontSize = 10.sp,
                                    color = Color(0xFF888888),
                                ),
                            )
                        }
                    }
                }
            }

            Spacer(modifier = Modifier.height(24.dp))

            // Hint
            Text(
                text = "← → to select  •  Enter to start  •  Esc to go back",
                style = monoStyle.copy(
                    fontSize = 9.sp,
                    color = Color(0xFF444466),
                ),
                textAlign = TextAlign.Center,
            )
        }
    }
}
